import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  useTennesseeMemphisDemoEnabled,
  useTropicalDemoKind,
} from 'utils/featureFlags';
import type { OrgEventAutomationRule } from 'hooks/orgEvents/orgEventAutomationHooks';
import {
  findFacilityDemoOrgEvent,
  findEpaGulfOrgEvent,
  findHeleneConeOrgEvent,
  findTnngMemphisOrgEvent,
  useOrgEvents,
} from 'hooks/orgEvents/orgEventHooks';
import { CorporateViewTabs } from 'components/MapPage/CorporateViewTabGroup';
import { AutomationRuleEditorDialog } from '../EventsTab/AutomationRuleEditorDialog';
import { OrgEventDetailsBody } from '../EventsTab/OrgEventDetailsBody';
import { CfaHeleneImpactSections } from '../EventsTab/CfaHeleneImpactSections';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MEMPHIS_LINKED_EVENT_NAME } from './memphisThreatNotification';
import { TNNG_MEMPHIS_LINKED_EVENT_NAME } from './tennesseeMemphisThreatNotification';
import { EPA_GULF_LINKED_EVENT_NAME } from './epaGulfThreatNotification';
import { getFacilityDemoPack } from './facilityDemoPack';
import { UpgradeHoverLock } from 'components/Notifications/LockedUpgradeOption';
import { useHubOnlyFlag } from 'utils/featureFlags';
import { TnngMemphisImpactSections } from './TnngMemphisImpactSections';

type MemphisThreatRichDetailsProps = {
  /** When provided, Create Incident is rendered at the bottom of the rich body. */
  onCreateIncident?: () => void;
  createIncidentLabel?: string;
  /** Linked threshold org event id (from IOC sourceItemId). */
  linkedOrgEventId?: string | null;
  assetItemDensity?: 'default' | 'comfortable';
};

/**
 * Hub HWCG-style expanded body: Event created → Assets at Risk → Response
 * Checklist → Create Incident.
 */
export function MemphisThreatRichDetails({
  onCreateIncident,
  createIncidentLabel = '+ Create Incident',
  linkedOrgEventId,
  assetItemDensity = 'default',
}: MemphisThreatRichDetailsProps) {
  const demoKind = useTropicalDemoKind();
  const hubOnly = useHubOnlyFlag();
  const tennesseeMemphisDemo = useTennesseeMemphisDemoEnabled();
  const cfaEnabled = demoKind != null;
  const [, setSearchParams] = useSearchParams();
  const { data: orgEvents = [] } = useOrgEvents({
    enabled: cfaEnabled || tennesseeMemphisDemo,
  });
  const facilityPack = getFacilityDemoPack(demoKind);
  const linkedEvent = tennesseeMemphisDemo
    ? findTnngMemphisOrgEvent(orgEvents)
    : (orgEvents.find((e) => e.id === linkedOrgEventId) ??
      (facilityPack
        ? findFacilityDemoOrgEvent(orgEvents, facilityPack)
        : demoKind === 'epa'
          ? findEpaGulfOrgEvent(orgEvents)
          : findHeleneConeOrgEvent(orgEvents)));
  const linkedEventName =
    linkedEvent?.name ||
    (facilityPack
      ? facilityPack.linkedEventName
      : demoKind === 'epa'
        ? EPA_GULF_LINKED_EVENT_NAME
        : tennesseeMemphisDemo
          ? TNNG_MEMPHIS_LINKED_EVENT_NAME
          : MEMPHIS_LINKED_EVENT_NAME);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [triggerRule, setTriggerRule] = useState<OrgEventAutomationRule | null>(
    null
  );

  const viewHeleneEventInEventsTab = () => {
    setEventModalOpen(false);
    setSearchParams(
      (params) => {
        params.set('openTab', CorporateViewTabs.Events);
        if (linkedEvent?.id) {
          params.set('eventId', linkedEvent.id);
        } else {
          params.delete('eventId');
        }
        return params;
      },
      { replace: true }
    );
  };

  return (
    <div className="tw:space-y-4 tw:text-sm tw:leading-snug">
      <div>
        <p className="tw:m-0 tw:text-foreground">
          <span className="tw:font-medium">Event created:</span>{' '}
          {cfaEnabled || tennesseeMemphisDemo ? (
            <button
              type="button"
              className="tw:font-medium tw:text-primary tw:underline tw:underline-offset-2 tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer"
              onClick={() =>
                demoKind === 'epa' || facilityPack
                  ? viewHeleneEventInEventsTab()
                  : setEventModalOpen(true)
              }
            >
              {linkedEvent?.name ?? linkedEventName}
            </button>
          ) : (
            <span className="tw:font-medium tw:text-primary">
              {linkedEventName}
            </span>
          )}
        </p>
      </div>

      {demoKind === 'cfa' ? (
        <CfaHeleneImpactSections
          responseChecklistActionIds={
            linkedEvent?.response_checklist_action_ids
          }
          responseChecklistItems={linkedEvent?.response_checklist_items}
          orgEventId={linkedEvent?.id}
          eventName={linkedEvent?.name ?? linkedEventName}
          includeAssets
          assetItemDensity={assetItemDensity}
        />
      ) : tennesseeMemphisDemo ? (
        <TnngMemphisImpactSections assetItemDensity={assetItemDensity} />
      ) : null}

      {onCreateIncident ? (
        <div className="tw:flex tw:justify-start">
          <UpgradeHoverLock locked={hubOnly}>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                if (hubOnly) return;
                onCreateIncident();
              }}
            >
              {createIncidentLabel}
            </Button>
          </UpgradeHoverLock>
        </div>
      ) : null}

      <Dialog open={eventModalOpen} onOpenChange={setEventModalOpen}>
        <DialogContent className="dark tw:sm:max-w-lg tw:text-foreground">
          <DialogHeader>
            <DialogTitle>Event Details</DialogTitle>
          </DialogHeader>
          {linkedEvent ? (
            <div className="tw:space-y-3">
              <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
                <Badge
                  className={cn(
                    'tw:shrink-0',
                    (linkedEvent.severity ?? '').toLowerCase() === 'high'
                      ? 'tw:border-transparent tw:bg-destructive/15 tw:text-destructive'
                      : (linkedEvent.severity ?? '').toLowerCase() === 'low'
                        ? 'tw:border-transparent tw:bg-emerald-400/20 tw:text-emerald-800 tw:dark:text-emerald-200'
                        : 'tw:border-transparent tw:bg-amber-400/25 tw:text-amber-800 tw:dark:text-amber-200'
                  )}
                >
                  {linkedEvent.severity || 'Medium'}
                </Badge>
                <Badge variant="outline" className="tw:shrink-0">
                  {linkedEvent.status || 'Active'}
                </Badge>
              </div>
              <p className="tw:m-0 tw:text-sm tw:font-medium">
                {linkedEvent.name}
              </p>
              <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                {[linkedEvent.type, linkedEvent.region]
                  .filter(Boolean)
                  .join(' · ') || 'Org event'}
              </p>
              <OrgEventDetailsBody
                event={linkedEvent}
                onTriggerRuleClick={setTriggerRule}
              />
            </div>
          ) : (
            <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
              {linkedEventName}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setEventModalOpen(false)}
            >
              Close
            </Button>
            {tennesseeMemphisDemo ? null : (
              <Button type="button" onClick={viewHeleneEventInEventsTab}>
                View in Events
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AutomationRuleEditorDialog
        rule={triggerRule}
        open={triggerRule != null}
        onOpenChange={(open) => {
          if (!open) setTriggerRule(null);
        }}
      />
    </div>
  );
}
